import {
  FunctionComponent,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { message as antMessage, Spin } from 'antd';
import { useLoading } from '@monorepo/react-hooks';
import { MailOtpSource, ReceivedMailState } from '@monorepo/types';
import { useStore } from '../../helpers/use-store';
import { FailedSendingMail } from './failed-sending-mail';
import { MailOtpStage } from './mail-otp-stage';
import { MailRejected } from './mail-rejected';
import { NoMoreRetries } from './no-more-retries';
import { NotReceivedMail } from './not-received-mail';

import './mail-confirmation.scss';

interface Props {
  verifiedCodeComponent: ReactNode;
  sendCodeStageHeader: string;
  sendCodeStageMessage: string;
  source: MailOtpSource;
  sourceId: string;
  displayName?: string;
}

export const MailConfirmation: FunctionComponent<Props> = ({
  verifiedCodeComponent,
  sendCodeStageHeader,
  sendCodeStageMessage,
  source,
  sourceId,
  displayName,
}) => {
  const [receivedMailState, setReceivedMailState] =
    useState<ReceivedMailState>();
  const { isLoading, setLoadingState } = useLoading();
  const { t } = useTranslation(['errors', 'mailConfirmation']);

  const {
    dataStore: { mailOtpStore },
  } = useStore();

  const getOtpStatus = useCallback(async () => {
    try {
      setLoadingState('Loading');
      const result = await mailOtpStore.getMailOtpStatus(sourceId);
      setReceivedMailState(result);
      setLoadingState('Loaded');
    } catch (e) {
      console.error('Error fetching OTP status:', e);
      setLoadingState('Error');
      setReceivedMailState(ReceivedMailState.ERROR);
      antMessage.error(t('errors:error_occurred_try_again'));
    }
  }, [mailOtpStore, sourceId, setLoadingState]);

  useEffect(() => {
    getOtpStatus();
  }, []);

  if (isLoading) {
    return <Spin />;
  }

  switch (receivedMailState) {
    case ReceivedMailState.NOT_RECEIVED:
      return (
        <NotReceivedMail
          header={sendCodeStageHeader}
          message={sendCodeStageMessage}
          source={source}
          sourceId={sourceId}
          setReceivedMailState={setReceivedMailState}
          displayName={displayName}
        />
      );

    case ReceivedMailState.RECEIVED:
      return (
        <MailOtpStage
          sourceId={sourceId}
          setReceivedMailState={setReceivedMailState}
          getOtpStatus={getOtpStatus}
        />
      );

    case ReceivedMailState.VERIFIED:
      return verifiedCodeComponent;

    case ReceivedMailState.NO_MORE_RETRIES:
      return <NoMoreRetries isLoading={isLoading} />;

    case ReceivedMailState.UNSUBSCRIBED:
      return (
        <MailRejected
          isLoading={isLoading}
          message={
            <Trans
              ns="mailConfirmation"
              i18nKey="unsubscribed_email_error"
              components={{ br: <br /> }}
            />
          }
        />
      );

    case ReceivedMailState.SOFT_BOUNCE:
      return (
        <MailRejected
          isLoading={isLoading}
          message={
            <Trans
              ns="mailConfirmation"
              i18nKey="soft_bounce_error"
              components={{ br: <br /> }}
            />
          }
        />
      );

    case ReceivedMailState.ERROR:
      return <FailedSendingMail isLoading={isLoading} />;

    default:
      return null;
  }
};
